import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Shield, User, UserCog, Truck, KeyRound, AlertTriangle, CheckCircle, X, Eye, EyeOff, Clock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'customer', company: '', phone: '', address: '' });
  const [formLoading, setFormLoading] = useState(false);

  // Password reset state
  const [resetRequests, setResetRequests] = useState([]);
  const [showReset, setShowReset] = useState(null); // email of user to reset
  const [resetPw, setResetPw] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  const getToken = () => localStorage.getItem('at1s_token');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const load = async () => {
    try {
      const [usersRes, requestsRes] = await Promise.all([
        axios.get(`${API}/api/auth/users`, authHeaders()),
        axios.get(`${API}/api/auth/reset-requests`, authHeaders()),
      ]);
      setUsers(usersRes.data);
      setResetRequests(requestsRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast({ title: 'Required', description: 'Name, email and password are required', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      await axios.post(`${API}/api/auth/users`, form, authHeaders());
      toast({ title: 'Created', description: `User ${form.email} created as ${form.role}` });
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'customer', company: '', phone: '', address: '' });
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setFormLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/auth/users/${showDelete}`, authHeaders());
      toast({ title: 'Deleted', description: `User ${showDelete} removed` });
      setShowDelete(null);
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleResetPassword = async () => {
    if (!resetPw || resetPw.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/api/auth/admin-reset-password`, {
        email: showReset,
        new_password: resetPw,
      }, authHeaders());
      toast({ title: 'Password Reset', description: `Password has been reset for ${showReset}` });
      setShowReset(null);
      setResetPw('');
      setShowResetPw(false);
      load(); // Refresh to update reset requests status
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to reset password', variant: 'destructive' });
    } finally { setResetLoading(false); }
  };

  const handleDismissRequest = async (email) => {
    try {
      await axios.delete(`${API}/api/auth/reset-requests/${encodeURIComponent(email)}`, authHeaders());
      toast({ title: 'Dismissed', description: `Reset request for ${email} dismissed` });
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const pendingRequests = resetRequests.filter(r => r.status === 'pending');
  const hasResetRequest = (email) => pendingRequests.some(r => r.email === email);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" /></div></Layout>;

  const admins = users.filter(u => u.role === 'admin');
  const customers = users.filter(u => u.role === 'customer');
  const deliveryUsers = users.filter(u => u.role === 'delivery');

  const UserRow = ({ u, icon: Icon, iconColor, badgeColor, badgeText }) => (
    <div key={u.email} className="px-5 py-3 flex items-center justify-between" data-testid={`user-row-${u.email}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 ${iconColor} rounded-full flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${iconColor.replace('bg-', 'text-').replace('100', '600')}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-800">{u.name}</p>
            {hasResetRequest(u.email) && (
              <span className="inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold" data-testid={`reset-badge-${u.email}`}>
                <KeyRound className="h-2.5 w-2.5" /> Reset Requested
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">{u.email} {u.company ? `— ${u.company}` : ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs ${badgeColor} px-2 py-0.5 rounded-full font-medium`}>{badgeText}</span>
        <button onClick={() => { setShowReset(u.email); setResetPw(''); setShowResetPw(false); }}
          className="p-1.5 rounded hover:bg-amber-50 text-amber-500 hover:text-amber-600" title="Reset Password"
          data-testid={`reset-password-${u.email}`}>
          <KeyRound className="h-4 w-4" />
        </button>
        <button onClick={() => setShowDelete(u.email)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-500"
          data-testid={`delete-user-${u.email}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-5" data-testid="user-management-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UserCog className="h-6 w-6 text-sky-600" /> User Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">{admins.length} admins, {customers.length} customers, {deliveryUsers.length} delivery</p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="bg-gradient-to-r from-sky-500 to-teal-500" data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-1" /> Add User
          </Button>
        </div>

        {/* Pending Password Reset Requests Banner */}
        {pendingRequests.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden" data-testid="reset-requests-section">
            <div className="px-5 py-3 bg-amber-100/50 border-b border-amber-200">
              <h2 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Password Reset Requests ({pendingRequests.length})
              </h2>
            </div>
            <div className="divide-y divide-amber-100">
              {pendingRequests.map(r => (
                <div key={r.email} className="px-5 py-3 flex items-center justify-between" data-testid={`reset-request-${r.email}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center">
                      <KeyRound className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{r.name || r.email}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        {r.email} <span className="text-amber-600 flex items-center gap-0.5"><Clock className="h-3 w-3" /> {formatDate(r.requested_at)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => { setShowReset(r.email); setResetPw(''); setShowResetPw(false); }}
                      className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8" data-testid={`resolve-reset-${r.email}`}>
                      <KeyRound className="h-3 w-3 mr-1" /> Reset Password
                    </Button>
                    <button onClick={() => handleDismissRequest(r.email)}
                      className="p-1.5 rounded hover:bg-amber-200 text-amber-500 hover:text-amber-700" title="Dismiss request"
                      data-testid={`dismiss-reset-${r.email}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Users */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-100 border-b">
            <h2 className="text-sm font-bold text-slate-600 flex items-center gap-2"><Shield className="h-4 w-4" /> Administrators</h2>
          </div>
          <div className="divide-y">
            {admins.map(u => <UserRow key={u.email} u={u} icon={Shield} iconColor="bg-sky-100" badgeColor="bg-sky-100 text-sky-700" badgeText="Admin" />)}
            {admins.length === 0 && <div className="p-5 text-center text-sm text-gray-400">No admin users</div>}
          </div>
        </div>

        {/* Customer Users */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-100 border-b">
            <h2 className="text-sm font-bold text-slate-600 flex items-center gap-2"><User className="h-4 w-4" /> Customers</h2>
          </div>
          <div className="divide-y">
            {customers.map(u => <UserRow key={u.email} u={u} icon={User} iconColor="bg-teal-100" badgeColor="bg-teal-100 text-teal-700" badgeText="Customer" />)}
            {customers.length === 0 && <div className="p-5 text-center text-sm text-gray-400">No customers registered yet</div>}
          </div>
        </div>

        {/* Delivery Users */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-100 border-b">
            <h2 className="text-sm font-bold text-slate-600 flex items-center gap-2"><Truck className="h-4 w-4" /> Delivery Personnel</h2>
          </div>
          <div className="divide-y">
            {deliveryUsers.map(u => <UserRow key={u.email} u={u} icon={Truck} iconColor="bg-blue-100" badgeColor="bg-blue-100 text-blue-700" badgeText="Delivery" />)}
            {deliveryUsers.length === 0 && <div className="p-5 text-center text-sm text-gray-400">No delivery personnel yet</div>}
          </div>
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <DialogDescription>Create a new admin, customer, or delivery account.</DialogDescription>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Doe" data-testid="form-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="user@company.com" data-testid="form-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Minimum 6 characters" data-testid="form-password" />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Optional" data-testid="form-company" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Optional" data-testid="form-phone" />
              </div>
            </div>
            <Button onClick={handleCreate} disabled={formLoading} className="w-full bg-gradient-to-r from-sky-500 to-teal-500"
              data-testid="form-submit">
              {formLoading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete User</DialogTitle></DialogHeader>
          <DialogDescription>Are you sure you want to permanently delete <strong>{showDelete}</strong>?</DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-user">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showReset} onOpenChange={() => { setShowReset(null); setResetPw(''); setShowResetPw(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-600" /> Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{showReset}</strong>. The user will need to use this new password to log in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showResetPw ? 'text' : 'password'}
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="pr-10"
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShowResetPw(!showResetPw)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                  {showResetPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {resetPw && resetPw.length < 6 && (
                <p className="text-xs text-red-500">Password must be at least 6 characters</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReset(null); setResetPw(''); setShowResetPw(false); }}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetLoading || resetPw.length < 6}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-reset-password">
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default UserManagement;
